import type { NewsStory } from './newsStory';

export const DEFAULT_RANKING_PROMPT = `Prioritize substantive world and US news, policy, and technology.
Deprioritize sports, celebrity gossip, and repetitive megathreads.
Keep a mix of sources; do not let one outlet dominate.`;

/** Compact numbered list for the Cursor agent (titles only). */
export function formatHeadlinesForCursor(stories: readonly NewsStory[]): string {
  return stories
    .map((s, i) => `${i + 1}. [${s.sourceLabel}] ${s.title.replace(/\s+/g, ' ').trim()}`)
    .join('\n');
}

export function buildNewsRankingMessage(
  stories: readonly NewsStory[],
  rankingPrompt: string,
  maxPick: number
): string {
  const headlines = formatHeadlinesForCursor(stories);
  const instructions = rankingPrompt.trim() || DEFAULT_RANKING_PROMPT;
  return [
    'Rank these news headlines for one reader.',
    '',
    'What they want:',
    instructions,
    '',
    `Pick up to ${maxPick} items, most relevant first.`,
    'Reply with one line per pick: only the list number (1, 2, 3, …). No other text.',
    '',
    'Headlines:',
    headlines,
  ].join('\n');
}

/** Parse agent reply: lines that are just integers (most relevant first). */
export function parseRankingReply(reply: string, storyCount: number): number[] {
  const seen = new Set<number>();
  const order: number[] = [];
  for (const line of reply.split('\n')) {
    const m = line.trim().match(/^(\d{1,3})\s*[\.\):\-]?$/);
    if (!m) continue;
    const n = Number(m[1]);
    if (!Number.isFinite(n) || n < 1 || n > storyCount || seen.has(n)) continue;
    seen.add(n);
    order.push(n);
  }
  return order;
}

export function applyRankingOrder(
  stories: readonly NewsStory[],
  oneBasedIndices: readonly number[]
): NewsStory[] {
  const picked: NewsStory[] = [];
  const used = new Set<number>();
  for (const n of oneBasedIndices) {
    const story = stories[n - 1];
    if (!story || used.has(n)) continue;
    used.add(n);
    picked.push(story);
  }
  return picked;
}
